import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAnalytics,
  fetchAnalytics,
  selecthasMoreAnalytics,
  selectAnalytics,
  selectAnalyticsLoading,
} from "../../../../store/slices/message-analytics";
import AuditAnalyticsHeader from "../audit-analytics-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import moment from "moment";
import { Body } from "../../../../components/main-body/body";
import classNames from "classnames";
import "../../../../components/main-body/body.css";
import { Progress } from "antd";
import AnalyticsImages from "../../../../assets/analytics-images/index";
import ApiScenariosManagement from "../../../../api/scenarios-management";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiSavedViews from "../../../../api/saved-views";
import AnalyticsCard from "./analytics-card/analytics-card";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ApiSubjectsDashboard from "../../../../api/subjects-dashboard";
import { selectSettings } from "../../../../store/slices/settings";
import { selectCurrentProject } from "../../../../store/slices/user";
import ScenarioDropOff from "../scenario-drop-off/scenario-drop-off";

const sortViews = (list = []) =>
  [...list].sort((a, b) => {
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) {
      return a.isDefault ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const DEFAULT_SORTING = {
  sortField: null,
  sortOrder: null,
};

const normalizeSorting = (sorting) => ({
  sortField: sorting?.sortField || null,
  sortOrder: sorting?.sortOrder || null,
});

const extractSavedViewState = (view, defaultFilters) => {
  const rawFilters = view?.filters || {};
  const rawSorting =
    view?.sorting ||
    rawFilters?.__sorting ||
    DEFAULT_SORTING;

  const nextFilters = { ...rawFilters };
  delete nextFilters.__sorting;

  return {
    filters:
      nextFilters && Object.keys(nextFilters).length
        ? nextFilters
        : defaultFilters,
    sorting: normalizeSorting(rawSorting),
  };
};

const MessagesAnalyticsTable = ({ navigation }) => {
  const analytics = useSelector(selectAnalytics);
  const hasMore = useSelector(selecthasMoreAnalytics);
  const isLoading = useSelector(selectAnalyticsLoading);
  const settings = useSelector(selectSettings);
  const currentProject = useSelector(selectCurrentProject);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const defaultFiltersRef = useRef({
    period: [
      {
        start: moment().subtract(30, "days").utc().toISOString(),
        end: moment().utc().toISOString(),
      },
    ],
  });

  const defaultFilters = defaultFiltersRef.current;

  const sortingOptions = [
    { value: "title", label: t("analytics.table.message") },
    { value: "lastRespondedAt", label: t("analytics.table.last_response") },
    { value: "scenarioTitle", label: t("analytics.table.scenario") },
    { value: "total", label: t("analytics.table.total") },
    { value: "completionRate", label: t("analytics.table.adherence") },
  ];

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [sorting, setSorting] = useState(DEFAULT_SORTING);

  const [scenariosList, setScenarioList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [selectedAnalytics, setSelectedAnalytics] = useState({});
  const [cohortsList, setCohortsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  const [savedViewsItems, setSavedViewsItems] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);

  const isSitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const SAVED_VIEW_SCOPE = "analytics_scenarios";

  const safeFilters = filters || defaultFilters;

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(
        r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })) || []
      );
      setSitesList(
        r.data.sites?.map((c) => ({ id: c.id, name: c.name })) || []
      );
    });
  };

  const handleSelectAnalytics = (message) => {
    handleNavigate(`/analytics/monitoring/scenarios/${message.id}`);
    setSelectedAnalytics(message);
    setOpen(true);
  };

  const closeTaskDrawer = () => {
    handleNavigate(`/analytics/monitoring/scenarios`);
    setOpen(false);
    setSelectedAnalytics({});
  };

  const fetchData = (
    currentPage = page,
    query = searchValue,
    currentFilters = safeFilters,
    reset = false,
    currentSorting = sorting,
  ) => {
    if (!currentFilters) return;

    if (reset) {
      dispatch(clearAnalytics());
    }

    dispatch(
      fetchAnalytics({
        query,
        page: currentPage,
        ...currentFilters,
        createdAtEnd:
          currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart:
          currentFilters.period?.[currentFilters.period.length - 1]?.start,
        sortField: currentSorting?.sortField || undefined,
        sortOrder: currentSorting?.sortOrder || undefined,
      })
    );

    setPage(currentPage + 1);
  };

  const loadSavedViews = useCallback(async () => {
    if (!currentProject?.id) {
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(DEFAULT_SORTING);
      setFiltersReady(true);
      return;
    }

    setSavedViewsLoading(true);

    try {
      const { data } = await ApiSavedViews.getSavedViews({
        projectId: currentProject.id,
        scope: SAVED_VIEW_SCOPE,
      });

      const list = sortViews(data?.savedViews || []);
      setSavedViewsItems(list);

      const defaultView = list.find((item) => item.isDefault);

      if (defaultView) {
        const nextState = extractSavedViewState(defaultView, defaultFilters);
        setSelectedViewId(defaultView.id);
        setFilters(nextState.filters);
        setSorting(nextState.sorting);
      } else {
        setSelectedViewId(null);
        setFilters(defaultFilters);
        setSorting(DEFAULT_SORTING);
      }
    } catch (error) {
      console.error("Failed to load saved views", error);
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(DEFAULT_SORTING);
    } finally {
      setSavedViewsLoading(false);
      setFiltersReady(true);
      setPage(1);
    }
  }, [currentProject?.id, defaultFilters]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    setPage(1);
    fetchData(1, value, safeFilters, true);
  };

  const fetchScenarioList = () => {
    ApiScenariosManagement.getScenariosSimple().then((r) => {
      setScenarioList(
        r.data.scenarios.map((s) => ({
          id: s.id,
          name: s.title,
        }))
      );
    });
  };

  const fetchSubjectList = () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectsList(
        r.data.subjects.map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  const updateFilterValue = (filter) => {
    setFilters(filter);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedViewId(null);
    setFilters(defaultFilters);
    setSorting(DEFAULT_SORTING);
    setPage(1);
  };

  const applySavedViewConfig = (savedState, viewId = null) => {
    const nextFilters =
      savedState?.filters && Object.keys(savedState.filters).length
        ? savedState.filters
        : defaultFilters;

    setSelectedViewId(viewId);
    setFilters(nextFilters);
    setSorting(normalizeSorting(savedState?.sorting));
    setPage(1);
  };

  const getIcon = (questionType) => {
    switch (questionType) {
      case "message:multi":
        return AnalyticsImages.checkBorder;
      case "message:single":
        return AnalyticsImages.radioChecked;
      case "message:date":
      case "message:time":
        return AnalyticsImages.date;
      case "message:picker":
      case "message:slider":
        return AnalyticsImages.typeSelect;
      case "message:input":
        return AnalyticsImages.text;
      default:
        return AnalyticsImages.typeSelect;
    }
  };

  const getQuestionTypeLabel = (questionType) => {
    switch (questionType) {
      case "message:single":
        return "Single choice";
      case "message:multi":
        return "Multi choice";
      case "message:picker":
        return "Picker";
      case "message:slider":
        return "Slider";
      case "message:date":
        return "Date";
      case "message:time":
        return "Time";
      case "message:input":
        return "Input";
      default:
        return "Unknown";
    }
  };

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const messageId = pathParts[pathParts.length - 1];

    if (messageId && messageId !== "scenarios") {
      const foundMessage = analytics.find((message) => message.id === messageId);

      if (foundMessage) {
        setSelectedAnalytics(foundMessage);
      } else {
        setSelectedAnalytics({ id: messageId });
      }

      setOpen(true);
    } else {
      setOpen(false);
      setSelectedAnalytics({});
    }
  }, [location.pathname, analytics]);

  useEffect(() => {
    fetchScenarioList();
    fetchSubjectList();
    getDashboardFilterItems();
  }, []);

  useEffect(() => {
    loadSavedViews();
  }, [loadSavedViews]);

  useEffect(() => {
    if (!filtersReady || !filters) return;

    dispatch(clearAnalytics());
    fetchData(1, searchValue, filters, true, sorting);
  }, [filtersReady, filters, sorting]);

  const handleTableChange = useCallback((pagination, tableFilters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!nextSorter?.field || !nextSorter?.order) {
      setSorting(DEFAULT_SORTING);
      return;
    }

    setSorting({
      sortField: nextSorter.field,
      sortOrder: nextSorter.order === "ascend" ? "ASC" : "DESC",
    });
  }, []);

  // Sorting is server-side now (fixes the bug where client-only sort would
  // re-order just the loaded page, not the full dataset). We still keep the
  // sorter UI state and forward sortField/sortOrder to the API.
  const sortedAnalytics = Array.isArray(analytics) ? analytics : [];

  const columns = [
    {
      title: t("analytics.table.message"),
      dataIndex: "title",
      sorter: true,
      render: (_, analyticsItem) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              flexShrink: 0,
              width: "40px",
              height: "40px",
              backgroundColor: "#EFEFEF",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={getIcon(analyticsItem.questionType)}
              alt="icon"
              style={{ width: "20px", height: "20px" }}
            />
          </div>
          <div>
            <span style={{ fontWeight: "bold" }}>
              {analyticsItem.title || "N/A"}
            </span>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {getQuestionTypeLabel(analyticsItem.questionType)}
            </div>
          </div>
        </div>
      ),
      key: "title",
      width: "40%",
      sortOrder:
        sorting.sortField === "title"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("analytics.table.last_response"),
      dataIndex: "lastRespondedAt",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>
          {analyticsItem.lastRespondedAt
            ? `${moment(analyticsItem.lastRespondedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A"}
        </span>
      ),
      key: "lastRespondedAt",
      width: "20%",
      sortOrder:
        sorting.sortField === "lastRespondedAt"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("analytics.table.scenario"),
      dataIndex: "scenarioTitle",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>{analyticsItem.scenarioTitle || "N/A"}</span>
      ),
      key: "scenarioTitle",
      width: "15%",
      sortOrder:
        sorting.sortField === "scenarioTitle"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("analytics.table.total"),
      dataIndex: "total",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>{(analyticsItem.totalCount + analyticsItem.pendingCount) || 0}</span>
      ),
      key: "total",
      width: "10%",
      sortOrder:
        sorting.sortField === "total"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
    {
      title: t("analytics.table.adherence"),
      dataIndex: "completionRate",
      sorter: true,
      render: (_, analyticsItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Progress
            type="circle"
            percent={analyticsItem.respondedPercentage || 0}
            size={25}
            strokeLinecap="butt"
            strokeWidth={25}
            strokeColor="#2D7FFA"
            railColor="rgba(45, 127, 250, 0.2)"
            format={() => ""}
          />
          {analyticsItem.respondedPercentage || 0}%
        </span>
      ),
      key: "completionRate",
      width: "15%",
      sortOrder:
        sorting.sortField === "completionRate"
          ? sorting.sortOrder === "ASC"
            ? "ascend"
            : "descend"
          : null,
    },
  ];

  const messageTypes = [
    { id: "message:single", name: "Single Choice" },
    { id: "message:multi", name: "Multi Choice" },
    { id: "message:picker", name: "Picker" },
    { id: "message:slider", name: "Slider" },
    { id: "message:date", name: "Date" },
    { id: "message:input", name: "Input" },
  ];

  const filterItems = [
    { name: "Cohorts", id: "cohortIds", children: cohortsList },
    {
      name: "Date peroid",
      id: "period",
      period: true,
      defaultValue: defaultFilters.period,
    },
    { name: "Message Type", id: "actionType", children: messageTypes },
    { name: "Scenario", id: "scenarioIds", children: scenariosList },
    ...(isSitesEnabled
      ? [{ name: "Sites", id: "siteIds", children: sitesList }]
      : []),
  ];

  const headerProps = useMemo(
    () => ({
      search: {
        value: searchValue,
        onChange: handleUpdateSearchValue,
        placeholder: t("assets.btn.search"),
      },
    }),
    [searchValue]
  );

  return (
    <Body disabledPaddings={true}>
      <AuditAnalyticsHeader
        title={t("analytics.scenarios")}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        header={headerProps}
        filters={safeFilters}
        sortingOptions={sortingOptions}
        savedViews={{
          enabled: true,
          projectId: currentProject?.id,
          scope: SAVED_VIEW_SCOPE,
          filters: safeFilters,
          sorting,
          defaultFilters,
          defaultSorting: DEFAULT_SORTING,
          filterItems,
          items: savedViewsItems,
          loading: savedViewsLoading,
          selectedViewId,
          onSelectedViewChange: setSelectedViewId,
          onApplySavedView: applySavedViewConfig,
          onResetFilters: resetFilters,
          onReloadSavedViews: loadSavedViews,
          disabled: isLoading || savedViewsLoading,
        }}
      />

      {/* LF360-1909. Moved here from Monitoring > Usage: progress through a scenario is
          scenario context, not a question about whether the app works. It sits between the
          filters and the table on purpose - the drop-off reads as an introduction to the
          rows below, and the table keeps the rest of the viewport. */}
      <ScenarioDropOff />

      <div className={classNames("lf-content")}>
        <InfinityTable
          data={sortedAnalytics}
          fetchData={() => fetchData(page, searchValue, safeFilters, false, sorting)}
          hasMore={hasMore}
          tableColumns={columns}
          isLoading={isLoading}
          before={navigation}
          onHandleRowClick={handleSelectAnalytics}
          onChange={handleTableChange}
        />
      </div>

      <AnalyticsCard
        analytics={selectedAnalytics}
        isOpen={isOpen}
        close={closeTaskDrawer}
        messageTypes={messageTypes}
        subjects={subjectsList}
        cohorts={cohortsList}
        initialPeriodFilter={
          safeFilters?.hasOwnProperty("period")
            ? { period: safeFilters.period }
            : {}
        }
        initialCohortsFilter={
          safeFilters?.hasOwnProperty("cohortIds")
            ? { cohortIds: safeFilters.cohortIds }
            : []
        }
      />
    </Body>
  );
};

export default MessagesAnalyticsTable;